import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";
import type { MonthlyTrend, CostByType } from "@/lib/maintenance-analytics";

interface MaintenanceCostAnalysisProps {
  monthlyTrends: MonthlyTrend[];
  costByType: CostByType;
}

const COLORS = {
  preventive: 'hsl(var(--success))',
  corrective: 'hsl(var(--destructive))',
  total: 'hsl(var(--primary))'
};

export function MaintenanceCostAnalysis({ monthlyTrends, costByType }: MaintenanceCostAnalysisProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const pieData = [
    { name: 'Preventiva', value: costByType.preventive.cost, color: COLORS.preventive },
    { name: 'Corretiva', value: costByType.corrective.cost, color: COLORS.corrective }
  ];

  const preventivePercentage = costByType.preventive.percentage;

  return (
    <div className="space-y-6">
      {/* Cost Distribution Pie Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Distribuição de Custos por Tipo</CardTitle>
          <CardDescription>
            Comparação entre manutenções preventivas e corretivas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value as number)} />
              </PieChart>
            </ResponsiveContainer>

            <div className="flex flex-col justify-center space-y-4">
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Manutenção Preventiva</p>
                <p className="text-2xl font-bold text-success">{formatCurrency(costByType.preventive.cost)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {preventivePercentage.toFixed(1)}% do total
                </p>
              </div>
              <div className="p-4 border rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Manutenção Corretiva</p>
                <p className="text-2xl font-bold text-destructive">{formatCurrency(costByType.corrective.cost)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {costByType.corrective.percentage.toFixed(1)}% do total
                </p>
              </div>
              {preventivePercentage < 40 && (
                <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
                  <p className="text-sm text-warning">
                    ⚠️ Baixo investimento em preventiva. Recomendado: mínimo 40%
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Trend Line Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Evolução Mensal de Custos</CardTitle>
          <CardDescription>
            Tendência de custos ao longo dos meses
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={monthlyTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey="month" 
                stroke="hsl(var(--foreground))"
                tick={{ fill: 'hsl(var(--foreground))' }}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value)}
                stroke="hsl(var(--foreground))"
                tick={{ fill: 'hsl(var(--foreground))' }}
              />
              <Tooltip 
                formatter={(value) => formatCurrency(value as number)}
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="totalCost" 
                stroke={COLORS.total}
                name="Custo Total"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Monthly Orders Bar Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Quantidade de Ordens de Serviço por Mês</CardTitle>
          <CardDescription>
            Comparação entre preventivas e corretivas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={monthlyTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey="month"
                stroke="hsl(var(--foreground))"
                tick={{ fill: 'hsl(var(--foreground))' }}
              />
              <YAxis 
                stroke="hsl(var(--foreground))"
                tick={{ fill: 'hsl(var(--foreground))' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Bar 
                dataKey="preventive" 
                fill={COLORS.preventive}
                name="Preventiva"
                stackId="a"
              />
              <Bar 
                dataKey="corrective" 
                fill={COLORS.corrective}
                name="Corretiva"
                stackId="a"
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
